import { ThirdPartyAppCredentialsPopupDetails } from '@coach-bot/data-access/shared/interfaces/third-party-app-credentials-popup-details.interface';
import { unloadOnOrphaned } from '@shared/extension';

enum InjectedScriptAction {
  GetThirdPartyAppCredentialsPopupDetails = 'getThirdPartyAppCredentialsPopupDetails',
  OpenThirdPartyAppCredentialsPopup = 'openThirdPartyAppCredentialsPopup',
  ThirdPartyAppCredentialsPopupDetailsRetrieved = 'thirdPartyAppCredentialsPopupDetailsRetrieved',
}

enum WindowEventType {
  FromContentScript = 'FROM_CONTENT_SCRIPT',
  FromInjectedScript = 'FROM_INJECTED_SCRIPT',
}

const FONTS_APPLY_DELAY = 2000;

class ThirdPartyAppCredentialsPopup {
  async open(username: string, password: string, popupDetails?: ThirdPartyAppCredentialsPopupDetails): Promise<void> {
    popupDetails = popupDetails || (await this.getPopupDetails());
    const iframeId = 'coachbot-third-party-credentials-popup';

    const iframeStyles = `<style>
      #${iframeId} {
        z-index: 100000;
        position: fixed;
        display: none;
        background-color: white;
        width: 420px;
        height: 128px;
        bottom: 1.5rem;
        right: .75rem;
        box-shadow: rgba(0, 0, 0, 0.25) 0px 10px 15px -3px, rgba(0, 0, 0, 0.25) 0px 4px 6px -4px;
        border-color: #e5e7eb;
        border-radius: 12px;
        border-width: 0px;
      }
    </style>`;

    const popupStyles = `<style>
      body {
        margin: 12px;
        font-family: Roboto,sans-serif;
        font-size: 13px;
        line-height: 18px;
      }
      .content-container {
        display: flex;
        align-items: center;
        gap: 12px;
        height: 100%;
      }
      .logo-container {
        display: flex;
        align-items: center;
        width: 10%;
      }
      .logo-container img {
        border-radius: 6px;
      }
      .close-icon-container {
        height: 100%;
        text-align: center;
      }
      .main-element {
        flex-grow: 1;
        margin: unset;
        width: 80%;
      }
      p {
        color: #212529de;
        line-height: 1.45;
        font-size: 14px;
      }
      .field {
        margin: unset;
      }
      .field.description {
        margin-bottom: 8px;
      }
      .field-label {
        font-weight: 500;
      }
      .logo {
        width: 100%;
      }
      .close-icon {
        cursor: pointer;
      }
    </style>`;

    const popupHtml = `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap" rel="stylesheet">

    <div class="content-container">
      <div class="logo-container">
        <img class="logo" src="${popupDetails.logoSrc}">
      </div>
      <div class="main-element">
        <p class="field description">${popupDetails.description}</p>
        <p class="field"><span class="field-label">${popupDetails.usernameLabel}:</span> ${username}</p>
        <p class="field"><span class="field-label">${popupDetails.passwordLabel}:</span> ${password}</p>
      </div>
      <div class="close-icon-container">
        <img class="close-icon" src="${popupDetails.closeIconSrc}">
      </div>
    </div>`;

    const iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.src = 'about:blank';

    iframe.onload = () => {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument) {
        return;
      }

      iframeDocument.head.insertAdjacentHTML('beforeend', popupStyles);
      iframeDocument.body.insertAdjacentHTML('beforeend', popupHtml);

      const closeButton = iframeDocument.querySelector('.close-icon');
      closeButton.addEventListener('click', () => {
        iframe.remove();
      });

      setTimeout(() => {
        iframe.style.display = 'block';
      }, FONTS_APPLY_DELAY);
    };

    document.body.insertAdjacentHTML('beforeend', iframeStyles);
    document.body.appendChild(iframe);
  }

  private async getPopupDetails(): Promise<ThirdPartyAppCredentialsPopupDetails> {
    return await new Promise(resolve => {
      const listener = (event: MessageEvent): void => {
        if (
          event.data.type === WindowEventType.FromContentScript &&
          event.data.action === InjectedScriptAction.ThirdPartyAppCredentialsPopupDetailsRetrieved
        ) {
          resolve(event.data.response);
        }
      };
      window.addEventListener('message', listener, false);
      window.postMessage(
        {
          type: WindowEventType.FromInjectedScript,
          action: InjectedScriptAction.GetThirdPartyAppCredentialsPopupDetails,
          detail: { url: window.location.href },
        },
        '*'
      );
    });
  }
}

const popup = new ThirdPartyAppCredentialsPopup();

const listener = (event: MessageEvent): void => {
  if (
    event.data.type === WindowEventType.FromInjectedScript &&
    event.data.action === InjectedScriptAction.OpenThirdPartyAppCredentialsPopup
  ) {
    popup.open(event.data.detail.username, event.data.detail.password, event.data.detail.popupDetails);
  }
};

window.addEventListener('message', listener);

unloadOnOrphaned('install-third-party-app-credentials-popup-script', () => {
  window.removeEventListener('message', listener);
});
